import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { PodcastEpisode, EpisodeStatus, PaginationMeta } from '@/types/episode.types';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { FilterChips, FilterCategory } from '@/components/FilterChips';
import { MobileEpisodeCard } from './MobileEpisodeCard';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { customToast } from '@/components/ui/custom-toast';
import './MobileEpisodeListing.css';

const FILTER_CATEGORY_STATUSES: Record<FilterCategory, EpisodeStatus[]> = {
  draft: [
    EpisodeStatus.DRAFT,
    EpisodeStatus.GENERATING_SCRIPTS,
    EpisodeStatus.GENERATING_SCRIPT,
    EpisodeStatus.SCRIPTS_APPROVED,
    EpisodeStatus.GENERATING_AUDIO,
    EpisodeStatus.MERGING,
    EpisodeStatus.PUBLISHING,
  ],
  needsReview: [
    EpisodeStatus.SCRIPTS_PENDING_REVIEW,
    EpisodeStatus.SCRIPT_READY,
    EpisodeStatus.AUDIO_PENDING_REVIEW,
    EpisodeStatus.AUDIO_READY,
  ],
  ready: [
    EpisodeStatus.MERGED,
    EpisodeStatus.AUDIO_APPROVED,
  ],
  published: [
    EpisodeStatus.PUBLISHED,
  ],
  failed: [
    EpisodeStatus.FAILED,
    EpisodeStatus.SCRIPT_FAILED,
    EpisodeStatus.AUDIO_FAILED,
    EpisodeStatus.PUBLISHING_FAILED,
  ],
  archived: [
    EpisodeStatus.ARCHIVED,
  ],
};

const SEARCH_DEBOUNCE_MS = 300;
const ITEMS_PER_PAGE = 15;

export const MobileEpisodeListing: React.FC = () => {
  const navigate = useNavigate();
  const { userCompanies, getCompanyColor } = useUserCompanies();
  const isMultiCompany = userCompanies.length > 1;

  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Set<FilterCategory>>(
    () => new Set<FilterCategory>(['draft', 'needsReview', 'ready', 'published', 'failed'])
  );
  const [page, setPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
    page: 1,
    limit: ITEMS_PER_PAGE,
    total: 0,
    totalPages: 0,
  });
  const [creatingEpisode, setCreatingEpisode] = useState(false);

  const requestCounterRef = useRef(0);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getSelectedStatuses = useCallback((): EpisodeStatus[] => {
    const statuses: EpisodeStatus[] = [];
    selectedFilters.forEach((category) => {
      statuses.push(...FILTER_CATEGORY_STATUSES[category]);
    });
    return statuses;
  }, [selectedFilters]);

  const loadEpisodes = useCallback(async (
    pageNum: number,
    search: string,
    statuses: EpisodeStatus[],
    append: boolean,
  ) => {
    if (statuses.length === 0) {
      setEpisodes([]);
      setPaginationMeta({ page: 1, limit: ITEMS_PER_PAGE, total: 0, totalPages: 0 });
      setPage(1);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const requestId = ++requestCounterRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const companyIds = userCompanies.length > 0
        ? userCompanies.map(c => c.id)
        : undefined;

      const result = await PodcastEpisodeService.getEpisodesPaginated({
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        search: search || undefined,
        orderBy: 'updatedAt',
        order: 'desc',
        companyIds,
        status: statuses,
      });

      if (requestId !== requestCounterRef.current) return;

      if (append) {
        setEpisodes(prev => [...prev, ...result.episodes]);
      } else {
        setEpisodes(result.episodes);
      }
      setPaginationMeta(result.meta);
      setPage(result.meta.page);
    } catch (err) {
      if (requestId !== requestCounterRef.current) return;
      console.error('Error loading episodes:', err);
      setError('Failed to load episodes. Please try again.');
    } finally {
      if (requestId === requestCounterRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [userCompanies]);

  // Reload from page 1 whenever search term or filters change
  useEffect(() => {
    loadEpisodes(1, searchTerm, getSelectedStatuses(), false);
  }, [loadEpisodes, searchTerm, selectedFilters, getSelectedStatuses]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const toggleFilter = (category: FilterCategory) => {
    setSelectedFilters(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    loadEpisodes(nextPage, searchTerm, getSelectedStatuses(), true);
  };

  const handleEpisodeClick = (id: string) => {
    navigate(`../episodes/${id}`);
  };

  const handleCreateEpisode = async (companyId?: string) => {
    try {
      setCreatingEpisode(true);
      const { id } = await PodcastEpisodeService.createDraftEpisode(undefined, companyId);
      customToast.success('Episode created');
      navigate(`../episodes/${id}`);
    } catch (err: any) {
      customToast.error('Failed to create episode', {
        description: err.message || 'Please try again',
      });
      setCreatingEpisode(false);
    }
  };

  const hasMore = paginationMeta.page < paginationMeta.totalPages;

  return (
    <div className="pm_mlist-root">
      {/* Header */}
      <div className="pm_mlist-header">
        <span className="pm_mlist-header-title">Episodes</span>
        {isMultiCompany ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" disabled={creatingEpisode}>
                {creatingEpisode ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                {creatingEpisode ? 'Creating...' : 'New'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {userCompanies.map((company) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => handleCreateEpisode(company.id)}
                >
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: getCompanyColor(company.id) || '#4A90E2' }}
                  />
                  {company.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button size="sm" onClick={() => handleCreateEpisode()} disabled={creatingEpisode}>
            {creatingEpisode ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            {creatingEpisode ? 'Creating...' : 'New'}
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="pm_mlist-search">
        <div className="pm_mlist-search-inner">
          <Search className="pm_mlist-search-icon h-4 w-4" />
          <input
            type="text"
            className="pm_mlist-search-input"
            placeholder="Search episodes..."
            value={searchInput}
            onChange={(e) => handleSearchInputChange(e.target.value)}
          />
        </div>
      </div>

      {/* Filter chips */}
      <div className="pm_mlist-filters">
        <FilterChips
          selectedFilters={selectedFilters}
          onToggle={toggleFilter}
          className="flex-wrap gap-1.5"
        />
      </div>

      {/* Episode list */}
      <div className="pm_mlist-list">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="pm_mlist-skeleton-card" />
          ))
        ) : error ? (
          <div className="pm_mlist-empty">{error}</div>
        ) : episodes.length === 0 ? (
          <div className="pm_mlist-empty">No episodes found</div>
        ) : (
          episodes.map((episode) => (
            <MobileEpisodeCard
              key={episode.id}
              episode={episode}
              onClick={() => handleEpisodeClick(episode.id)}
            />
          ))
        )}
      </div>

      {/* Footer: load more + count */}
      {!loading && episodes.length > 0 && (
        <div className="pm_mlist-footer">
          {hasMore && (
            <button
              className="pm_mlist-load-more"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </button>
          )}
          <span className="pm_mlist-count">
            Showing {episodes.length} of {paginationMeta.total} episodes
          </span>
        </div>
      )}
    </div>
  );
};
